import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export const GOOGLE_ROLE_KEY = "alr_google_role";

export const startGoogleLogin = (role) => {
  sessionStorage.setItem(GOOGLE_ROLE_KEY, role);
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + `/dashboard?g_role=${encodeURIComponent(role)}`;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const GoogleCallback = ({ hash, search }) => {
  const { loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const done = useRef(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sessionId = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    const role = new URLSearchParams(search).get("g_role") || sessionStorage.getItem(GOOGLE_ROLE_KEY) || "franchisee";
    sessionStorage.removeItem(GOOGLE_ROLE_KEY);
    window.history.replaceState(null, "", window.location.pathname);
    loginWithGoogle(sessionId, role)
      .then(() => navigate("/", { replace: true }))
      .catch((e) => setError(e.message || "Google sign-in failed. Please try again."));
  }, [hash, search, loginWithGoogle, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A] px-6 font-body text-white" data-testid="google-callback">
      {error ? (
        <div className="w-full max-w-md rounded-2xl border border-[#333] bg-[#1A1A1A] p-8 text-center" data-testid="google-login-error">
          <p className="text-[11px] font-bold tracking-[3px] text-[#4CCC06]">GOOGLE SIGN-IN</p>
          <p className="mt-3 text-[15px] leading-6">{error}</p>
          <button onClick={() => navigate("/?login=1", { replace: true })} data-testid="google-error-back" className="mt-6 w-full rounded-lg bg-[#4CCC06] py-3.5 text-[13px] font-extrabold tracking-[1.6px] text-black">BACK TO SIGN IN</button>
        </div>
      ) : <Loader2 className="animate-spin text-[#4CCC06]" size={28} />}
    </div>
  );
};

// Intercepts the #session_id fragment Emergent Auth appends after Google sign-in.
export const OAuthGate = ({ children }) => {
  const { hash, search } = useLocation();
  if (hash?.includes("session_id=")) return <GoogleCallback hash={hash} search={search} />;
  return children;
};

const GoogleMark = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

export const GoogleButton = ({ role, disabled, onBlocked }) => (
  <button type="button" data-testid="google-login-btn" onClick={() => (disabled ? onBlocked() : startGoogleLogin(role))}
    className="mt-3 flex w-full items-center justify-center gap-3 rounded-lg border border-[#3a3a3a] bg-white py-3.5 text-[14px] font-semibold text-[#1f1f1f] transition-opacity hover:opacity-90" style={disabled ? { opacity: 0.5 } : undefined}>
    <GoogleMark /> Continue with Google
  </button>
);
